import classNames from 'classnames'

import Button, { ButtonTypes } from '~/components/atoms/Button/Button'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Link from '~/components/atoms/Link/Link'
import Text, { TextSizes } from '~/components/atoms/Text/Text'

import styles from './ReportCard.module.css'

export interface ReportCardProps {
  _id: string
  title: string
  subtitle: string
  primaryCategory: {
    name: string
    slug: string
  }
  summary?: string
  leadImage: ImageProps
  isPdfType: boolean
  slug: string
  variant?:
    | 'default'
    | 'bigFeature'
    | 'snippet'
    | 'consise'
    | 'inlineFeature'
    | 'inlineFeatureCompact'
    | 'featurePanel'
  addBackground?: boolean
  blockTitle?: string
  matchGridHeight?: boolean
  onButtonClick?: (e: React.MouseEvent) => void
  ctaText?: string
  ctaUrl?: string
  ctaAuthForm?: 'signup' | 'login'
  titleNode?: React.ReactNode
  footerContent?: React.ReactNode
}

export default function ReportCard({
  title,
  subtitle,
  summary,
  leadImage,
  isPdfType,
  slug,
  variant = 'default',
  addBackground = false,
  blockTitle,
  matchGridHeight,
  onButtonClick,
  ctaText,
  ctaUrl,
  ctaAuthForm,
  titleNode,
  footerContent,
}: ReportCardProps) {
  const buttonText =
    ctaText ?? (isPdfType ? 'Download the Report' : 'Read the Report')
  const url = ctaUrl ?? `/market-reports/${slug}`

  const renderButton = () => {
    let buttonProps = {
      link: url,
    } as any

    if (onButtonClick) {
      buttonProps = {
        button: { onClick: onButtonClick, type: ButtonTypes.button },
      }
    }

    if (ctaAuthForm) {
      buttonProps = { launchAuthForm: ctaAuthForm }
    }

    return (
      <Button {...buttonProps} iconLeft className={styles.button}>
        {buttonText}
      </Button>
    )
  }
  const isBigFeature = Boolean(variant === 'bigFeature')
  const isInlineFeatureCompact = Boolean(variant === 'inlineFeatureCompact')
  const isFeaturePanel = Boolean(variant === 'featurePanel')
  const renderButtonInContent =
    isBigFeature || isInlineFeatureCompact || isFeaturePanel
  const sizes = isBigFeature
    ? '(max-width: 480px) 100vw, (max-width: 768px) 50vw, 1000px'
    : '(max-width: 480px) 100vw, (max-width: 768px) 50vw, 512px'

  return (
    <div
      className={classNames(
        styles.base,
        styles[variant],
        addBackground && styles.withBg,
        matchGridHeight && styles.heightFull
      )}
    >
      {blockTitle && (
        <Text
          size={TextSizes.primaryTitleSml}
          Tag="h4"
          className={styles.blockTitle}
        >
          {blockTitle}
        </Text>
      )}
      <div className={styles.media}>
        <Link href={url}>
          <Image
            asset={leadImage.asset}
            alt={title}
            sizes={sizes}
            className={styles.mainImage}
          />
        </Link>
      </div>
      <div className={styles.text}>
        <div className={styles.textInner}>
          {isBigFeature && (
            <Text size={TextSizes.primaryTitleMed} Tag="h3">
              {subtitle}
            </Text>
          )}
          <Text
            size={
              isBigFeature
                ? TextSizes.magSecondaryTitleLrg
                : TextSizes.primaryTitleSml
            }
            Tag="h2"
          >
            {titleNode ?? title}
          </Text>
          {!isBigFeature && (
            <>
              <Text size={TextSizes.bodyMed} Tag="h3">
                {subtitle}
              </Text>
              {summary && variant !== 'snippet' && variant !== 'consise' && (
                <Text Tag="p" size={TextSizes.bodySml}>
                  {summary}
                </Text>
              )}
            </>
          )}
          {renderButtonInContent ? renderButton() : null}
          {footerContent && (
            <Text size={TextSizes.bodyMed} Tag="div" className={styles.footer}>
              {footerContent}
            </Text>
          )}
        </div>
      </div>
      {!renderButtonInContent ? renderButton() : null}
    </div>
  )
}
